import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import Button, {
  ButtonIconPosition,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import FormInput, {
  InputType,
} from '@/components/BaseLibrary/FormInput/FormInput';
import { IconName } from '@/components/Icon/Icon';
import type { userLocationInterface } from '@/hooks/geo';
import usStates from '@/utils/usStates';
import { sleep } from '@/utils/utils';
import { InputName, zipCodeValidator } from '@/utils/validation/inputs';

import useStyles from './Location.styles';

export interface LocationProps extends userLocationInterface {
  closeLocation: () => void;
  isOnDark: boolean;
}

const locationSchema = z.object({
  ...zipCodeValidator,
});

type LocationData = z.infer<typeof locationSchema>;

const Location = ({
  closeLocation,
  isOnDark,
  setUserLocation,
  userLocation,
}: LocationProps) => {
  const { classes, cx } = useStyles({ isOnDark });

  const locationClass = cx(classes.location, isOnDark && classes.locationDark);

  const [isUpdatingLocation, setIsUpdatingLocation] = useState<boolean>(false);

  const {
    trigger,
    register,
    formState: { errors, isSubmitSuccessful, isValid },
    handleSubmit,
  } = useForm<LocationData>({
    resolver: zodResolver(locationSchema),
  });

  const isDisabled =
    Object.keys(errors).length > 0 ||
    isSubmitSuccessful ||
    isUpdatingLocation ||
    !isValid;

  // Scroll to top of screen while updating location
  // In useEffect because it interferes with react state change re-render
  useEffect(() => {
    if (isUpdatingLocation) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isUpdatingLocation]);

  // Updates the user's state from either a submitted zip code or their current location then closes the drawer with animation
  async function submitZipCode(zipCode?: string) {
    const zipcodes = (await import('zipcodes')).default;
    setIsUpdatingLocation(true);
    if (zipCode) {
      const state = zipcodes.lookup(zipCode)?.state;
      setUserLocation({
        state,
        zipCode,
      });
    } else {
      setUserLocation();
    }

    await sleep(1000);
    closeLocation();
    setIsUpdatingLocation(false);
  }

  const onSubmit: SubmitHandler<LocationData> = async (data: LocationData) => {
    await submitZipCode(data.zipCode);
  };
  return (
    <div className={locationClass}>
      <div className={classes.locationInner}>
        <h1 className={classes.locationTitle}>
          We use your location to provide localized solar offers and incentives.
        </h1>
        <div className={classes.inputWrapper}>
          <FormInput
            autoFocus
            label='Zip code'
            placeholder='Enter your Zip code'
            testId='zip-input'
            type={InputType.ZIP}
            inputMode='numeric'
            isOnDark={isOnDark}
            error={errors[InputName.ZIP_CODE]?.message}
            name={InputName.ZIP_CODE}
            register={register}
            onBlur={(event) => {
              if (event.target.value !== '') {
                trigger();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit(onSubmit)(event);
              }
            }}
          />
        </div>
        <div className={classes.buttonContainer}>
          <Button
            label='Update'
            isOnDark={isOnDark}
            variant={ButtonVariant.PRIMARY}
            disabled={isDisabled}
            onClick={handleSubmit(onSubmit)}
          />
          {/** biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome */}
          {userLocation &&
            userLocation.state &&
            userLocation.state in usStates && (
              <>
                <div className={classes.lineContainer}>
                  <div className={classes.line} />
                  <span>or</span>
                  <div className={classes.line} />
                </div>
                <Button
                  label='Use my current location'
                  variant={ButtonVariant.SECONDARY}
                  isOnDark={isOnDark}
                  iconName={IconName.PIN}
                  iconPosition={ButtonIconPosition.LEFT}
                  onClick={() => submitZipCode()}
                  disabled={isUpdatingLocation}
                />
              </>
            )}
        </div>
      </div>
    </div>
  );
};

export default Location;
